/* SOAR Facets — collection filter sidebar.
   Listens for form change + active-facet chip clicks, fetches the section
   via the Shopify Section Rendering API, swaps the section HTML, and
   updates browser history. */

(function () {
  if (customElements.get('soar-facets')) return;

  class SoarFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-soar-facets-form]');
      if (!this.form) return;

      this.form.addEventListener('change', this.onChange.bind(this));
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitFilters();
      });

      // active-facet chips and "Clear all" use AJAX too
      this.bindFacetLinks();

      // Mobile drawer toggle wiring (find buttons outside this element)
      const section = this.closest('soar-collection-main');
      if (section) {
        const openers = section.querySelectorAll('[data-soar-filters-toggle]');
        const closers = section.querySelectorAll('[data-soar-filters-close]');
        openers.forEach((el) => el.addEventListener('click', () => {
          section.dataset.filtersOpen = 'true';
          el.setAttribute('aria-expanded', 'true');
          document.body.style.overflow = 'hidden';
        }));
        closers.forEach((el) => el.addEventListener('click', () => {
          section.dataset.filtersOpen = 'false';
          openers.forEach((o) => o.setAttribute('aria-expanded', 'false'));
          document.body.style.overflow = '';
        }));
      }
    }

    bindFacetLinks() {
      const section = this.closest('soar-collection-main');
      if (!section) return;
      section.addEventListener('click', (e) => {
        const link = e.target.closest('[data-soar-facet-link]');
        if (!link) return;
        e.preventDefault();

        // Merged size chip: remove ALL of its underlying values in one click.
        if (link.dataset.soarRemoveParam) {
          const param = link.dataset.soarRemoveParam;
          const vals = (link.dataset.soarRemoveVals || '').split('|');
          const cur = new URL(window.location.href);
          const kept = new URLSearchParams();
          for (const [k, v] of cur.searchParams.entries()) {
            if (k === param && vals.includes(v)) continue;
            kept.append(k, v);
          }
          const url = new URL(cur.pathname, window.location.origin);
          url.search = kept.toString();
          this.fetchAndSwap(url);
          return;
        }

        const url = new URL(link.href, window.location.origin);
        this.fetchAndSwap(url);
      });
    }

    onChange() {
      // Debounce slightly so consecutive checkbox clicks coalesce
      clearTimeout(this._t);
      this._t = setTimeout(() => this.submitFilters(), 80);
    }

    syncGroups() {
      // Mirror each merged group control (size/color/type) onto its hidden per-value
      // inputs so FormData serializes every underlying value (Shopify OR's them).
      this.form.querySelectorAll('[data-soar-merge-group]').forEach((ctrl) => {
        const label = ctrl.closest('label');
        if (!label) return;
        label.querySelectorAll('[data-soar-group-value]').forEach((v) => {
          v.checked = ctrl.checked;
        });
      });
    }

    submitFilters() {
      this.syncGroups();
      const formData = new FormData(this.form);
      const params = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (value === '' || value == null) continue;
        params.append(key, value);
      }

      const action = this.form.getAttribute('action') || window.location.pathname;
      const url = new URL(action, window.location.origin);
      url.search = params.toString();
      this.fetchAndSwap(url);
    }

    async fetchAndSwap(url) {
      const section = this.closest('soar-collection-main');
      if (!section) return;

      section.dataset.facetsLoading = 'true';

      const fetchUrl = new URL(url.toString());
      fetchUrl.searchParams.set('section_id', this.sectionId);

      try {
        const res = await fetch(fetchUrl.toString(), { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        const html = await res.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const incoming = doc.querySelector('soar-collection-main');
        if (!incoming) throw new Error('Incoming section not found in response.');

        // Replace inner children of the existing element rather than the element
        // itself so we keep this <soar-facets> reference and its bindings.
        section.innerHTML = incoming.innerHTML;

        // Swapped-in cards carry the .soar-reveal (opacity:0) class but the reveal
        // observer won't re-run for them — mark them revealed immediately so the
        // filtered grid is visible right away.
        section.querySelectorAll('.soar-reveal').forEach((el) => el.classList.add('revealed'));

        // Re-upgrade nested custom elements (browser auto-upgrades on insert).
        // Update history: strip section_id from the visible URL.
        const visible = new URL(url.toString());
        visible.searchParams.delete('section_id');
        const finalUrl = visible.pathname + (visible.search || '') + (visible.hash || '');
        window.history.pushState({ soarFacets: true }, '', finalUrl);

        // The replaced subtree no longer has our soar-facets connected callback
        // bound to the new form. Re-bind by re-running the connectedCallback
        // on the new <soar-facets> instance inside the swapped HTML.
        const newFacets = section.querySelector('soar-facets');
        if (newFacets && newFacets !== this) {
          // The new element will run its own connectedCallback on insert.
        }
      } catch (err) {
        console.error('[soar-facets]', err);
      } finally {
        section.dataset.facetsLoading = 'false';
      }
    }
  }

  customElements.define('soar-facets', SoarFacets);

  /* Dual-thumb price range slider. Drives two hidden fields (the real Shopify
     filter params) and dispatches a bubbling change so SoarFacets refreshes. */
  class SoarPriceSlider extends HTMLElement {
    connectedCallback() {
      this.min = parseFloat(this.dataset.min || '0');
      this.max = parseFloat(this.dataset.max || '0');
      this.symbol = this.dataset.symbol || '$';
      this.rMin = this.querySelector('[data-soar-price-range="min"]');
      this.rMax = this.querySelector('[data-soar-price-range="max"]');
      this.fill = this.querySelector('[data-soar-price-fill]');
      this.outMin = this.querySelector('[data-soar-price-out-min]');
      this.outMax = this.querySelector('[data-soar-price-out-max]');
      this.fieldMin = this.querySelector('[data-soar-price-field="min"]');
      this.fieldMax = this.querySelector('[data-soar-price-field="max"]');
      if (!this.rMin || !this.rMax || this.max <= this.min) return;

      // Keep a small gap so the thumbs never cross.
      this.gap = Math.max(1, Math.round((this.max - this.min) * 0.02));

      this.rMin.addEventListener('input', () => this.onInput('min'));
      this.rMax.addEventListener('input', () => this.onInput('max'));
      this.rMin.addEventListener('change', () => this.commit());
      this.rMax.addEventListener('change', () => this.commit());
      this.render();
    }

    onInput(which) {
      let lo = parseFloat(this.rMin.value);
      let hi = parseFloat(this.rMax.value);
      if (which === 'min' && lo > hi - this.gap) { lo = Math.max(this.min, hi - this.gap); this.rMin.value = lo; }
      if (which === 'max' && hi < lo + this.gap) { hi = Math.min(this.max, lo + this.gap); this.rMax.value = hi; }
      this.render();
    }

    render() {
      const lo = parseFloat(this.rMin.value);
      const hi = parseFloat(this.rMax.value);
      const span = (this.max - this.min) || 1;
      const loPct = ((lo - this.min) / span) * 100;
      const hiPct = ((hi - this.min) / span) * 100;
      if (this.fill) { this.fill.style.left = loPct + '%'; this.fill.style.right = (100 - hiPct) + '%'; }
      if (this.outMin) this.outMin.textContent = this.symbol + Math.round(lo);
      if (this.outMax) this.outMax.textContent = this.symbol + Math.round(hi);
      // Raise the min thumb above the max thumb when they bunch at the top end,
      // otherwise the max thumb covers it and you can't drag back down.
      this.rMin.style.zIndex = loPct > 90 ? 5 : 3;
    }

    commit() {
      const lo = Math.round(parseFloat(this.rMin.value));
      const hi = Math.round(parseFloat(this.rMax.value));
      if (this.fieldMin) this.fieldMin.value = lo > this.min ? lo : '';
      if (this.fieldMax) this.fieldMax.value = hi < this.max ? hi : '';
      const form = this.closest('form');
      if (form) form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  if (!customElements.get('soar-price-slider')) {
    customElements.define('soar-price-slider', SoarPriceSlider);
  }

  // Handle back/forward navigation — full page reload (simplest, robust)
  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.soarFacets) {
      window.location.reload();
    }
  });
})();
